/**
 * Download statistics of a package's builds (registry.pure.dev counts every
 * tarball it serves: internal/registry backend/downloads.ts, read through the
 * broker's GET /v1/publish/downloads). Pure helpers for the Registry page's
 * builds table and per-package sparkline; unit-tested by downloads.test.ts.
 *
 * What the numbers are: non-bot downloads of the build's tarballs (the build,
 * its pointer tarball and each platform package), counted by the registry
 * Worker. Installs served from a client's own cache (npm/pnpm/bun store)
 * never reach the registry and are not counted. Bots and scanners are kept
 * apart (`bots`) and never in `total`.
 */

export interface DownloadSummary {
  total: number;
  d7: number;
  d30: number;
  bots: number;
  firstSeen: number | null;
  lastSeen: number | null;
  byKind: Record<string, number>;
  byClient: Record<string, number>;
  byPlatform: Record<string, number>;
}

export interface DownloadsReport {
  name: string;
  /** Per BUILD version. A build nobody downloaded since counting started
   *  has no entry. */
  builds: Record<string, DownloadSummary>;
  /** Non-bot downloads per UTC day, oldest first. */
  daily: Array<{ day: string; n: number }>;
}

/** The broker answered but the registry could not (counting is advisory:
 *  the builds table still renders without it). */
export type DownloadsState =
  { ok: true; report: DownloadsReport } | { ok: false; error: string };

export const NO_DOWNLOADS: DownloadSummary = {
  total: 0,
  d7: 0,
  d30: 0,
  bots: 0,
  firstSeen: null,
  lastSeen: null,
  byKind: {},
  byClient: {},
  byPlatform: {},
};

export function summaryFor(
  report: DownloadsReport | null | undefined,
  buildVersion: string,
): DownloadSummary {
  return report?.builds[buildVersion] ?? NO_DOWNLOADS;
}

/** "never" / "just now" / "3d ago" - when a build was last downloaded. */
export function lastSeenLabel(
  lastSeen: number | null,
  now = Date.now(),
): string {
  if (lastSeen === null) return "never";
  const s = Math.max(0, Math.round((now - lastSeen) / 1000));
  if (s < 90) return "just now";
  const m = Math.round(s / 60);
  if (m < 90) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 36) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** The table cell: "12 (3 / 9)" = total (last 7d / last 30d). */
export function downloadsCell(s: DownloadSummary): string {
  return s.total === 0 ? "0" : `${s.total} (${s.d7} / ${s.d30})`;
}

/** The tooltip: first/last seen and the client and platform split. */
export function downloadsTitle(s: DownloadSummary, now = Date.now()): string {
  if (s.total === 0 && s.bots === 0) return "No downloads counted";
  const part = (m: Record<string, number>) =>
    Object.entries(m)
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => `${k} ${v}`)
      .join(", ") || "-";
  return [
    `${s.total} downloads (last 7 days ${s.d7}, last 30 days ${s.d30}), ${s.bots} bot/scanner requests not counted`,
    `last downloaded ${lastSeenLabel(s.lastSeen, now)}${s.firstSeen ? `, first ${new Date(s.firstSeen).toISOString().slice(0, 10)}` : ""}`,
    `clients: ${part(s.byClient)}`,
    `files: ${part(s.byKind)}`,
    `platforms: ${part(s.byPlatform)}`,
  ].join("\n");
}

/** SVG polyline points for the per-day series (oldest left), scaled to
 *  width x height with a 1px margin; a flat series sits on the baseline. */
export function sparklinePoints(
  daily: Array<{ n: number }>,
  width: number,
  height: number,
): string {
  if (daily.length === 0) return "";
  const max = Math.max(1, ...daily.map((d) => d.n));
  const step = daily.length > 1 ? (width - 2) / (daily.length - 1) : 0;
  return daily
    .map((d, i) => {
      const x = 1 + i * step;
      const y = height - 1 - (d.n / max) * (height - 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

/** "47 downloads in the last 60 days" for the sparkline's caption. */
export function sparklineCaption(daily: Array<{ n: number }>): string {
  const sum = daily.reduce((s, d) => s + d.n, 0);
  return `${sum} download${sum === 1 ? "" : "s"} in the last ${daily.length} days`;
}
